import Hls from 'hls.js';

import messagingService from 'services/MessagingService/MessagingService';
import { UPDATE_VIDEO_METRICS } from 'services/MessagingService/EventNames';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';


const KILOBYTE_AS_BYTES = 1000;

class SmartxPlayerMetrics {
  constructor(hlsInstance) {
    this.messagingService = messagingService();
    this.hlsInstance = hlsInstance;
    this.events = { bitrate: [] };
    this.hlsInstance.on(Hls.Events.FRAG_BUFFERED, this.handleFragBuffered);
    this.hlsInstance.on(Hls.Events.FPS_DROP, this.handleFPSdrop);
    this.hlsInstance.on(Hls.Events.FRAG_PARSING_METADATA, this.handleParseMetaData);
    const second = 2000;
    this._fpsInterval = setInterval(this._getMetrics, second);
  }

  destructor = () => {
    this.hlsInstance.off(Hls.Events.FRAG_BUFFERED, this.handleFragBuffered);
    this.hlsInstance.off(Hls.Events.FPS_DROP, this.handleFPSdrop);
    this.hlsInstance.off(Hls.Events.FRAG_PARSING_METADATA, this.handleParseMetaData);
    clearInterval(this._fpsInterval);
  };

  _fpsInterval = null;
  _frames = 0;
  _resolution = '';
  _fps = 0;
  _fpsArray = [];

  _getMetrics = () => {
    if (!this?.hlsInstance?._media) {
      return;
    }

    const videoEl = this.hlsInstance._media;
    const { videoWidth, videoHeight } = videoEl;
    const { totalVideoFrames = 0 } = videoEl?.getVideoPlaybackQuality?.() || {};
    const resolution = `${videoWidth}x${videoHeight}`;
    const viewerFps = (totalVideoFrames - this._frames) / 2;

    this._fpsArray.push(viewerFps);
    if (this._fpsArray.length > 5) {
      this._fpsArray.shift();
    }

    const averageFps = Math.round(this._fpsArray.reduce((a, b) => a + b, 0) / this._fpsArray.length);

    if (this._resolution !== resolution || this._fps !== viewerFps) {
      const videoMetrics = {
        viewerFps: averageFps,
        resolution,
      };
      this.messagingService.emit(UPDATE_VIDEO_METRICS, videoMetrics);
    }

    this._resolution = resolution;
    this._frames = totalVideoFrames;
    this._fps = viewerFps;
  };

  handleParseMetaData = (_, data) => {
    this.processID3(data.samples);
  };

  processID3(samples) {
    for (const i in samples) {
      if (Object.hasOwn(samples, i)) {
        const parsedTag = [];
        samples[i].data.forEach((element) => parsedTag.push(String.fromCharCode(element)));
        const tagAsString = parsedTag.join('');
        this.extractMetaData(tagAsString);
      }
    }
  }

  extractMetaData = (data) => {
    const lastChunkTimeIndex = data.indexOf('LAST-CHUNK-TIME-START-MS');
    const broadcasterFPS = data.indexOf('broadcasterFPS');
    const delay = Date.now() - parseInt(data.substring(data.lastIndexOf(' ') + 1, data.length), 10);
    const delayInSeconds = Math.round(delay / SECOND_AS_MILLISECONDS);

    if (lastChunkTimeIndex !== -1) {
      this.messagingService.emit(UPDATE_VIDEO_METRICS, { delay: delayInSeconds });
    } else if (broadcasterFPS !== -1) {
      const start = data.indexOf('{');
      const end = data.lastIndexOf('}');
      const metadata = JSON.parse(data.slice(start, end + 1));

      this.messagingService.emit(UPDATE_VIDEO_METRICS, {
        cqb: metadata.broadcasterCameraQuality.oneMinuteRate,
        fps: {
          origin: Math.round(metadata.originFPS.oneMinuteRate),
          broadcaster: Math.round(metadata.broadcasterFPS.oneMinuteRate),
        },
        df: {
          origin: Math.round(metadata.originBitrate.loss),
          broadcaster: Math.round(metadata.broadcasterFPS.droppedFrames),
        },
        br: {
          origin: Math.round(metadata.originBitrate.received / KILOBYTE_AS_BYTES),
          broadcaster: `${Math.round(metadata.broadcasterBitrate.sent / KILOBYTE_AS_BYTES)} Kb`,
        },
      });
    }
  };

  handleFragBuffered = (_, data) => {
    const kb = 1024;
    const bitrate = Math.round(data.stats.bwEstimate / kb / kb);
    this.events.bitrate.push({ bitrate });
    let maxBitrate = this.events.bitrate[0].bitrate;
    let sumBitrate = 0;
    const BITRATE_RANGE = 10;

    for (let i = this.events.bitrate.length - 1, j = 1; i >= 0; i -= 1, j += 1) {
      maxBitrate = Math.max(maxBitrate, this.events.bitrate[i].bitrate);
      sumBitrate += this.events.bitrate[i].bitrate;
      if (j === BITRATE_RANGE) {
        break;
      }
    }

    const CHUNK_COUNT = this.events.bitrate.length < BITRATE_RANGE ? this.events.bitrate.length : BITRATE_RANGE;
    const metricsObject = {
      viewerBR: Math.round(sumBitrate / CHUNK_COUNT),
      mbr: maxBitrate,
    };
    this.messagingService.emit(UPDATE_VIDEO_METRICS, metricsObject);
  };

  handleFPSdrop = (_, data) => {
    const metricsObject = { viewerDropFps: data.currentDropped };
    this.messagingService.emit(UPDATE_VIDEO_METRICS, metricsObject);
  };
}

export default SmartxPlayerMetrics;
